import { maxSlotBudget, travelCost } from './event';
import { baht } from './format';
import type {
  MusicEvent,
  OrganiserPrefs,
  Performer,
  PerformerPrefs,
} from '../types/marketplace';

export interface Fit {
  score: number;
  reason: string;
}

const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

export function eventFit(event: MusicEvent, prefs: PerformerPrefs, me: Performer): Fit {
  const overlap = event.genres.filter((g) => prefs.genres.includes(g));
  const top = maxSlotBudget(event);
  const fitsFee = top >= Math.max(prefs.minFee, me.price * 0.85);
  const near = event.distanceKm <= prefs.maxTravelKm;
  const typeMatch = prefs.eventTypes.includes(event.type);
  const score = clamp(
    46 + Math.min(overlap.length, 2) * 14 + (fitsFee ? 12 : -8) + (near ? 8 : -10) + (typeMatch ? 6 : 0),
    38,
    98,
  );
  const parts: string[] = [];
  parts.push(overlap.length ? `Wants ${overlap[0].toLowerCase()}` : `${event.genres[0]} ${event.type.toLowerCase()}`);
  parts.push(fitsFee ? 'within your fee' : 'below your usual fee');
  if (!near) parts.splice(1, 0, `${event.distanceKm} km away`);
  return { score, reason: parts.slice(0, 2).join(' · ') };
}

export function performerFit(p: Performer, event: MusicEvent, prefs: OrganiserPrefs): Fit {
  const overlap = p.genres.filter((g) => event.genres.includes(g));
  const budget = maxSlotBudget(event);
  const cost = p.price + travelCost(p, p.distanceKm);
  const affordable = cost <= budget * 1.05;
  const near = p.distanceKm <= prefs.maxDistanceKm;
  const rated = p.rating >= prefs.minRating;
  const knows = p.keys.find((k) =>
    prefs.mustHaveKeys.some((m) => k.title.toLowerCase().includes(m.toLowerCase())),
  );
  const score = clamp(
    44 + Math.min(overlap.length, 2) * 14 + (affordable ? 12 : -8) + (near ? 6 : -8) + (rated ? 6 : 0) + (knows ? 6 : 0),
    38,
    98,
  );
  const parts: string[] = [];
  parts.push(
    overlap.length ? `Plays ${overlap.slice(0, 2).join(' & ').toLowerCase()}` : `${p.genres[0]} ${p.actType.toLowerCase()}`,
  );
  if (knows) parts.push(`knows “${knows.title}”`);
  else parts.push(affordable ? 'fits your budget' : `${baht(cost - budget)} over budget`);
  return { score, reason: parts.join(' · ') };
}
